import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.js';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/datetime/format.js';
import {
  COOLOFF_OPTIONS,
  RG_QUERY_KEYS,
  activeExclusion,
  setSelfExclusion,
  useSelfExclusions,
  type ExclusionKind,
} from '@/features/rg/api.js';

/**
 * Responsible gambling — the player's own stop button.
 *
 * The backend has had self-exclusion since migration 0010: table, repo,
 * internal resolver, an enforced gate, and a route the gateway already exposes
 * to the `client` audience. The player app never called any of it, so a player
 * who wanted to stop had to ask support to stop them — which inverts the point
 * of the control.
 *
 * Two deliberate shapes here:
 *
 *  - Confirmation is required and states the consequence in full, because this
 *    is one-way. There is no "shorten it" control: lifting early is an operator
 *    action by design, and offering an undo would make the control worthless to
 *    the person who most needs it.
 *  - The indefinite option is separated from the timed ones rather than sitting
 *    at the end of the same list, so it cannot be picked by momentum.
 */
export function ResponsibleGamblingSection() {
  const { t, i18n } = useTranslation('profile');
  const qc = useQueryClient();
  const query = useSelfExclusions();
  const active = activeExclusion(query.data?.items);

  const [pending, setPending] = React.useState<{
    kind: ExclusionKind;
    durationHours: number | null;
  } | null>(null);

  const mutation = useMutation({
    mutationFn: setSelfExclusion,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RG_QUERY_KEYS.selfExclusions });
      toast.success(t('rg.applied'));
      setPending(null);
    },
    onError: () => {
      toast.error(t('rg.failed'));
    },
  });

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[15px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
        {t('rg.title')}
      </h2>

      <Card>
        <div className="flex flex-col gap-4 p-5">
          <p className="text-[12px] leading-relaxed text-[var(--text-secondary)]">
            {t('rg.intro')}
          </p>

          {active ? (
            <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-4">
              <div className="text-[14px] font-semibold text-[var(--text-primary)]">
                {active.kind === 'self_exclusion' ? t('rg.activeExclusion') : t('rg.activeCooloff')}
              </div>
              <div className="mt-1 text-[12px] text-[var(--text-secondary)]">
                {active.expiresAt
                  ? t('rg.activeUntil', {
                      when: formatDateTime(active.expiresAt, i18n.language),
                    })
                  : t('rg.activeIndefinite')}
              </div>
              <div className="mt-2 text-[12px] text-[var(--text-muted)]">{t('rg.liftHint')}</div>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <div className="text-[13px] font-semibold text-[var(--text-primary)]">
                  {t('rg.cooloffTitle')}
                </div>
                <div className="text-[12px] text-[var(--text-secondary)]">
                  {t('rg.cooloffSubtitle')}
                </div>
                <div className="mt-1 flex flex-wrap gap-2">
                  {COOLOFF_OPTIONS.map((hours) => (
                    <Button
                      key={hours}
                      size="sm"
                      variant="secondary"
                      onClick={() => setPending({ kind: 'cooloff', durationHours: hours })}
                    >
                      {t(`rg.cooloffOption.${hours}`)}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="border-t border-[var(--border-subtle)] pt-4">
                <div className="text-[13px] font-semibold text-[var(--text-primary)]">
                  {t('rg.excludeTitle')}
                </div>
                <div className="mt-1 text-[12px] text-[var(--text-secondary)]">
                  {t('rg.excludeSubtitle')}
                </div>
                <Button
                  className="mt-3"
                  size="sm"
                  variant="danger"
                  onClick={() => setPending({ kind: 'self_exclusion', durationHours: null })}
                >
                  {t('rg.excludeButton')}
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>

      {pending && (
        <Dialog open onOpenChange={(v) => { if (!v) setPending(null); }}>
          <DialogContent className="max-w-sm">
            <DialogTitle>
              {pending.kind === 'self_exclusion' ? t('rg.confirmExcludeTitle') : t('rg.confirmCooloffTitle')}
            </DialogTitle>
            <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
              {pending.kind === 'self_exclusion'
                ? t('rg.confirmExcludeBody')
                : t('rg.confirmCooloffBody', { option: t(`rg.cooloffOption.${pending.durationHours}`) })}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setPending(null)}>
                {t('common.cancel', { ns: 'common' })}
              </Button>
              <Button
                variant="danger"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate(pending)}
              >
                {t('rg.confirmButton')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
